import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
} from '@mui/material'
import { ArrowBack, Email, Language, Security, Science } from '@mui/icons-material'

const About: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            About BlueBit TrialFinder
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Making Clinical Trial Discovery Accessible to Everyone
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Our Mission
          </Typography>
          <Typography paragraph>
            BlueBit TrialFinder was created to bridge the gap between patients seeking treatment options and the 
            thousands of clinical trials actively recruiting participants. We believe that everyone deserves easy 
            access to potentially life-changing research opportunities.
          </Typography>
          <Typography paragraph>
            Our mission is to make clinical trial discovery as simple as taking a photo or having a conversation. 
            By leveraging AI technology, we transform complex medical information into actionable trial matches, 
            empowering patients to take control of their healthcare journey.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            What We Do
          </Typography>
          <Typography paragraph>
            BlueBit TrialFinder combines three powerful capabilities:
          </Typography>
          
          <Grid container spacing={3} sx={{ my: 2 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Science sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    AI-Powered Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Advanced AI extracts and organizes health data from photos, voice, or text
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Language sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Trial Matching
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search ClinicalTrials.gov database to find relevant research studies
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Privacy-Focused
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your health data is secure and protected with encryption
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Who We Help
          </Typography>
          <Typography component="ul">
            <li><strong>Patients with chronic conditions</strong> seeking new treatment options</li>
            <li><strong>People with rare diseases</strong> looking for specialized research studies</li>
            <li><strong>Caregivers and family members</strong> helping loved ones find trials</li>
            <li><strong>Anyone interested in advancing medical science</strong> through clinical trial participation</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Our Technology
          </Typography>
          <Typography paragraph>
            We leverage cutting-edge artificial intelligence from OpenAI to:
          </Typography>
          <Typography component="ul">
            <li>Extract medical information from photos of documents, prescriptions, and lab results</li>
            <li>Process voice descriptions of symptoms and medical history</li>
            <li>Organize unstructured health data into comprehensive profiles</li>
            <li>Match health conditions to relevant clinical trials</li>
          </Typography>
          <Typography paragraph>
            All this happens securely, with your privacy as our top priority. We use encryption, secure storage, 
            and follow HIPAA-compliant practices to protect your sensitive health information.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Our Commitment to Privacy
          </Typography>
          <Typography paragraph>
            We understand the sensitive nature of health information. That's why:
          </Typography>
          <Typography component="ul">
            <li>Your data is encrypted both in transit and at rest</li>
            <li>We never sell your personal health information</li>
            <li>You maintain full control over your data</li>
            <li>We encourage you to exclude personal identifiers from health entries</li>
            <li>You can export or delete your data at any time</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Medical Disclaimer
          </Typography>
          <Typography paragraph sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1 }}>
            <strong>Important:</strong> BlueBit TrialFinder is an informational tool and is NOT a substitute for 
            professional medical advice, diagnosis, or treatment. Always consult with qualified healthcare professionals 
            regarding medical conditions and clinical trial participation. We do not endorse any specific clinical 
            trials or guarantee eligibility for any research study.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Support & Contact
          </Typography>
          <Typography paragraph>
            We're here to help! If you have questions, feedback, or need assistance:
          </Typography>
          
          <Stack spacing={2} sx={{ my: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Email color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email Support
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      info@bluebithealth.com
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Language color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Website
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      www.bluebithealth.com
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            Pricing
          </Typography>
          <Grid container spacing={3} sx={{ my: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Free Tier
                  </Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                    $0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Perfect for trying the app
                  </Typography>
                  <Typography component="ul" variant="body2">
                    <li>100 AI API calls included</li>
                    <li>Full access to all features</li>
                    <li>No time restrictions</li>
                    <li>No credit card required</li>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: 2, borderColor: 'primary.main' }}>
                <CardContent>
                  <Chip label="Best Value" color="primary" size="small" sx={{ mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Premium Lifetime
                  </Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                    $20
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}one-time
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Lifetime access to premium features
                  </Typography>
                  <Typography component="ul" variant="body2">
                    <li>Unlimited trial searches</li>
                    <li>Export health data</li>
                    <li>Batch eligibility analysis</li>
                    <li>Additional API credits available</li>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 BlueBit Health LLC. All rights reserved.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            BlueBit TrialFinder • Version 1.0.0
          </Typography>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/register')}
          >
            Get Started Free
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default About

